<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import ChevronLeft from "@/components/icons/ChevronLeft.vue";
import ChevronRight from "@/components/icons/ChevronRight.vue";
import Close from "@/components/icons/Close.vue";

const props = defineProps({
	images: {
		type: Array,
		required: true,
	},
	initialIndex: {
		type: Number,
		default: 0,
	},
	isOpen: {
		type: Boolean,
		required: true,
	},
});

const emit = defineEmits(["close"]);

const slider = ref(null);

const onSwiper = (swiper) => {
	slider.value = swiper;
	slider.value.slideTo(props.initialIndex);
};

const closeLightbox = () => {
	emit("close");
};

const handleKeydown = (event) => {
	if (!props.isOpen) return;

	if (event.key === "Escape") {
		closeLightbox();
	}
};

watch(
	() => props.isOpen,
	(newValue) => {
		if (newValue) {
			nextTick(() => {
				if (slider.value && slider.value.swiper) {
					slider.value.swiper.slideTo(props.initialIndex);
				}
			});
		}
	},
);

onMounted(() => {
	document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
	document.removeEventListener("keydown", handleKeydown);
});
</script>

<template>
	<Transition
		enter-active-class="transition-opacity duration-300 ease-out"
		enter-from-class="opacity-0"
		enter-to-class="opacity-100"
		leave-active-class="transition-opacity duration-200 ease-in"
		leave-from-class="opacity-100"
		leave-to-class="opacity-0"
	>
		<div
			v-if="isOpen"
			class="fixed inset-0 z-50 flex items-center justify-center bg-white text-black"
		>
			<button
				class="absolute top-16 right-16 z-10 flex items-center justify-center transition-opacity hover:opacity-50 sm:top-24 sm:right-24"
				@click="closeLightbox"
				aria-label="Close lightbox"
			>
				<Close class="size-24" />
			</button>

			<div
				class="md:h6 h8 font-primary-condensed absolute top-16 left-16 z-10 sm:top-24 sm:left-24"
			>
				{{ slider?.activeIndex + 1 }}/{{ images.length }}
			</div>

			<div
				class="relative flex h-full w-full items-center justify-center p-16"
			>
				<Swiper @swiper="onSwiper" class="size-full">
					<SwiperSlide v-for="(image, index) in images" :key="index">
						<div class="flex size-full items-center justify-center">
							<img
								:src="image"
								:alt="`Product image ${index + 1}`"
								class="max-h-[80vh] max-w-full object-contain sm:max-h-full"
							/>
						</div>
					</SwiperSlide>
				</Swiper>
			</div>

			<button
				v-if="images.length > 1"
				class="absolute top-1/2 left-8 z-10 hidden size-48 -translate-y-1/2 items-center justify-center transition-all hover:opacity-50 md:flex"
				@click="slider.slidePrev()"
				aria-label="Previous image"
			>
				<ChevronLeft class="size-32" />
			</button>

			<button
				v-if="images.length > 1"
				class="absolute top-1/2 right-8 z-10 hidden size-48 -translate-y-1/2 items-center justify-center transition-all hover:opacity-50 md:flex"
				@click="slider.slideNext()"
				aria-label="Next image"
			>
				<ChevronRight class="size-32" />
			</button>

			<div
				v-if="images.length > 1"
				class="absolute bottom-16 left-1/2 z-10 flex -translate-x-1/2 gap-8 sm:bottom-48"
			>
				<button
					v-for="(image, index) in images"
					:key="index"
					class="size-48 overflow-hidden border-2 transition-all hover:opacity-80"
					:class="{
						'border-black': slider?.activeIndex === index,
						'border-black/10': slider?.activeIndex !== index,
					}"
					@click="slider.slideTo(index)"
					:aria-label="`Go to image ${index + 1}`"
				>
					<img
						:src="image"
						:alt="`Thumbnail ${index + 1}`"
						class="size-full object-cover"
					/>
				</button>
			</div>
		</div>
	</Transition>
</template>
